/**
 * R5: hidden adaptive difficulty. A private strength estimate, never shown, read
 * from saved attempts only (no new storage). It moves like an Elo rating: a clean
 * first-try solve of a puzzle is a win against that puzzle's rating; anything with
 * a mistake, hint or reveal is a loss. Only the first attempt at each puzzle counts,
 * so replays and review repeats never inflate it.
 */
interface StrengthAttempt {
  puzzleId: string;
  startedAt: number;
  outcome: string;
  solvedFirstAttempt: boolean;
  wrongMoves: unknown[];
  hintsUsed: number;
  rating: number;
}
export const adaptiveStart = 1300;
const floor = 600,
  ceiling = 2700;

export function hiddenStrength(attempts: StrengthAttempt[]) {
  const firsts = new Map<string, StrengthAttempt>();
  for (const a of [...attempts].sort((a, b) => a.startedAt - b.startedAt)) {
    if (!Number.isFinite(a.rating) || !Number.isFinite(a.startedAt) || !Array.isArray(a.wrongMoves))
      continue;
    const finished = a.outcome === 'solved' || a.outcome === 'revealed';
    // Opening and leaving an untouched position is not an attempt at its tactic.
    if (!finished && !a.wrongMoves.length && !a.hintsUsed) continue;
    if (!firsts.has(a.puzzleId)) firsts.set(a.puzzleId, a);
  }
  let strength = adaptiveStart,
    counted = 0;
  for (const a of firsts.values()) {
    const expected = 1 / (1 + 10 ** ((a.rating - strength) / 400));
    const k = counted < 10 ? 48 : counted < 30 ? 32 : 24;
    strength += k * ((a.solvedFirstAttempt ? 1 : 0) - expected);
    strength = Math.min(ceiling, Math.max(floor, strength));
    counted++;
  }
  return Math.round(strength);
}

/**
 * Puzzles a little below the estimate solve cleanly about 60–70% of the time,
 * which keeps practice moving without feeling easy.
 */
export function adaptiveRange(strength: number): [number, number] {
  const centre = strength - 50;
  return [Math.max(400, Math.round(centre - 150)), Math.min(3000, Math.round(centre + 150))];
}
